import styles from './AccountSettings.module.scss'
import { Title } from './sections/Title'
import React, { useEffect } from 'react'
import { CustomTextfield } from '../../../shared-components/textfield/CustomTextField'
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Grid,
  Paper,
  Typography,
  Collapse,
  IconButton,
  Tabs,
  Tab
} from '@mui/material'
import {
  ExpandMore,
  ExpandLess,
  Send,
  CloudUpload,
  Delete,
  InsertDriveFile
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import Notiflix from 'notiflix'
import instance from '../../../../providers/axios.config'
import { ETicketPriorites, typeResponse } from '../../../../utils/types'
import { supportCategoryTypes, ticketPriorities } from '../../../../utils/constants'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../redux/reducers/rootReducer'
import SupportTickersList from './SupportTicketsList'

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`support-tabpanel-${index}`}
      aria-labelledby={`support-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

export const Support = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const [tabValue, setTabValue] = React.useState(0)
  const [selectedCategory, setSelectedCategory] = React.useState('');
  const [selectedPriority, setSelectedPriority] = React.useState<keyof typeof ETicketPriorites>('low');
  const [message, setMessage] = React.useState('')
  const [explanationExpanded, setExplanationExpanded] = React.useState(true)
  const [attachments, setAttachments] = React.useState<File[]>([])
  const [dragActive, setDragActive] = React.useState(false)

  const account = useSelector((state: RootState) => state.user.usersData)

  // Check URL parameters on mount and when location changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ticketParam = params.get('ticket');
    
    // If ticket parameter exists, switch to "My Tickets" tab
    if (ticketParam) {
      setTabValue(1);
    } else if (params.get('category')) {
      supportCategoryTypes(account?.role.slug || '').forEach(element => {
        const item = element.items.find(item => item.id === params.get('category'));
        if (item) {
          setSelectedCategory(item.id);
        }
      });
    }
  }, [location.search]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    
    // Clear URL parameters when manually switching tabs
    if (location.search && newValue !== 1) {
      navigate(location.pathname, { replace: true });
    }
  };

  const handleSendMessage = async () => {
    if (!account) return;
    try {
      Notiflix.Loading.standard({ svgColor: '#c11b1e' })

      // Create FormData for file uploads
      const formData = new FormData()
      formData.append('message', message)
      formData.append('category', selectedCategory)

      let selectedPriority: keyof typeof ETicketPriorites | null = null;
      supportCategoryTypes(account.role.slug).forEach(element => {
        const item = element.items.find(item => item.id === selectedCategory);
        if (item) {
          selectedPriority = element.priority || null;
        }
      });
      if (selectedPriority) {
        formData.append('priority',selectedPriority || ticketPriorities[ETicketPriorites.low]);
      }
      
      // Add attachments
      attachments.forEach((file, index) => {
        formData.append(`attachments[${index}]`, file)
      })

      const resp = await instance.post(`support`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }) as typeResponse;

      if ((resp as any).status === 'success') {
        Notiflix.Notify.success(t('ticket_created_successfully'))
        setMessage('')
        setSelectedCategory('')
        setSelectedPriority('low')
        setAttachments([])
        
        // Switch to "My Tickets" tab after creating ticket
        setTabValue(1);
      } else {
        Notiflix.Report.failure(
          'Error',
          `${resp?.message}`,
          'OK'
        )
      }
    } catch (error) {
      console.error('Error creating ticket:', error)
      Notiflix.Report.failure(
        'Error',
        'An error occurred while creating the ticket',
        'OK'
      )
    } finally {
      Notiflix.Loading.remove()
    }
  }

  const handleChangeSupportCategory = (event: any) => {
    const value = event.target.value;
    setSelectedCategory(value);
  };

  // File handling functions
  const handleDrag = React.useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [])

  const handleDrop = React.useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(Array.from(e.dataTransfer.files))
    }
  }, [])

  const handleFiles = (files: File[]) => {
    const validFiles = files.filter(file => {
      // Check file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        Notiflix.Notify.warning(t('file_too_large', { name: file.name }))
        return false
      }
      
      // Check file type
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain',
        'image/jpeg',
        'image/jpg', 
        'image/png',
        'image/gif',
        'application/zip',
        'application/x-zip-compressed'
      ]
      
      if (!allowedTypes.includes(file.type)) {
        Notiflix.Notify.warning(t('file_type_not_allowed', { name: file.name }))
        return false
      }
      
      return true
    })

    // Check total number of files
    if (attachments.length + validFiles.length > 5) {
      Notiflix.Notify.warning(t('max_files_exceeded'))
      return
    }

    setAttachments(prev => [...prev, ...validFiles])
  }

  const removeFile = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index))
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  // Get selected category details
  const getSelectedCategoryDetails = () => {
    if (!selectedCategory) return null;
    if (!account?.role) return null;

    for (const group of supportCategoryTypes(account.role.slug)) {
      const item = group.items.find(item => item.id === selectedCategory);
      if (item) {
        return {
          name: t(item.name),
          explanation: t(`${item.name}_explanation`),
          group: t(group.group)
        };
      }
    }
    return null;
  };

  const categoryDetails = getSelectedCategoryDetails();

  return (
    <div className={styles.container}>
      <div className={styles.topContainer}>
        <Title title={t('support_center')} />
      </div>

      <div className={styles.content}>
        <div className={styles.title}>
          <Alert severity="info">{t('support_center_description')}</Alert>
        </div>

        <Box sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="support tabs">
              <Tab label={t('create_ticket')} />
              <Tab label={t('my_tickets')} />
            </Tabs>
          </Box>

          {/* Create Ticket Tab */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              {/* Left side - Form */}
              <Grid item xs={12} md={selectedCategory ? 8 : 12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel id="select-support-category-type-label">
                      {t('select_category')}
                    </InputLabel>
                    <Select
                      size="small"
                      className={styles.textfield}
                      labelId="select-support-category-type-label"
                      id="select-support-category-type"
                      label={t('select_category')}
                      value={selectedCategory}
                      onChange={handleChangeSupportCategory}
                    >
                      {supportCategoryTypes(account?.role.slug || '').map((group, groupIndex) => [
                        <ListSubheader key={`group-${groupIndex}`} sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          {t(group.group)}
                        </ListSubheader>,
                        ...group.items.map((item) => (
                          <MenuItem key={item.id} value={item.id} sx={{ pl: 4 }}>
                            {t(item.name)}
                          </MenuItem>
                        ))
                      ])}
                    </Select>
                  </FormControl>

                  {/* <FormControl fullWidth>
                    <InputLabel id="select-support-priority">
                      {t('select_priority')}
                    </InputLabel>
                    <Select
                      size="small"
                      className={styles.textfield}
                      labelId="select-support-priority"
                      id="select-support-priority"
                      value={selectedPriority}
                      onChange={(e: any) => setSelectedPriority(e.target.value)}
                    >
                      {ticketPriorities.map((priority) => (
                        <MenuItem key={`ticket-priority-${priority}`} value={priority}>{t(`priority_${priority}`)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl> */}

                  <CustomTextfield
                    className={styles.textfield}
                    size='small'
                    name='message'
                    rows={6}
                    multiline
                    variant='outlined'
                    label={t('describe_your_issue')}
                    value={message}
                    onChange={(e: any) => setMessage(e.target.value)}
                    placeholder={selectedCategory ? t('describe_your_ticket_issue_detailed') : t('select_category_first')}
                  />

                  {/* File Upload Section */}
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      {t('attach_files')} ({t('optional')})
                    </Typography>
                    
                    {/* Drag and Drop Area */}
                    <Box
                      sx={{
                        border: `2px dashed ${dragActive ? 'primary.main' : 'grey.300'}`,
                        borderRadius: 2,
                        p: 3,
                        textAlign: 'center',
                        bgcolor: dragActive ? 'action.hover' : 'grey.50',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: 'action.hover'
                        }
                      }}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                      onClick={() => document.getElementById('file-input')?.click()}
                    >
                      <input
                        id="file-input"
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.zip"
                        onChange={(e) => e.target.files && handleFiles(Array.from(e.target.files))}
                        style={{ display: 'none' }}
                      />
                      <Box display={'flex'} flexDirection={'row'} gap={1} alignItems={'center'} justifyContent={'center'}>
                        <CloudUpload sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          {t('drag_drop_files_here')}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {t('or_click_to_browse')}
                      </Typography>
                      <Typography variant="subtitle2" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        {t('max_file_size_10mb')} • {t('max_5_files')}
                      </Typography>
                      <Typography variant="subtitle2" color="text.secondary" display="block">
                        {t('allowed_formats')}: PDF, DOC, DOCX, TXT, JPG, PNG, ZIP
                      </Typography>
                    </Box>

                    {/* File List */}
                    {attachments.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                          {t('attached_files')} ({attachments.length}/5)
                        </Typography>
                        {attachments.map((file, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              p: 1,
                              mb: 1,
                              border: '1px solid',
                              borderColor: 'grey.300',
                              borderRadius: 1,
                              bgcolor: 'background.paper'
                            }}
                          >
                            <InsertDriveFile sx={{ mr: 1, color: 'primary.main' }} />
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <Typography variant="body2" noWrap>
                                {file.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatFileSize(file.size)}
                              </Typography>
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => removeFile(index)}
                              sx={{ color: 'error.main' }}
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>

                  <Box>
                    <Button
                      variant='contained'
                      disabled={!message || !selectedCategory || !selectedPriority}
                      onClick={() => handleSendMessage()}
                      sx={{ minWidth: 140 }}
                      startIcon={<Send />}
                    >
                      {t('create_ticket')}
                    </Button>
                  </Box>
                </Box>
              </Grid>

              {/* Right side - Category explanation */}
              {selectedCategory && categoryDetails && (
                <Grid item xs={12} md={4}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 2,
                      backgroundColor: 'grey.50',
                      border: '1px solid',
                      borderColor: 'primary.light',
                      maxHeight: { xs: 'auto', md: '400px' },
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 1,
                        cursor: { xs: 'pointer', md: 'default' }
                      }}
                      onClick={() => setExplanationExpanded(!explanationExpanded)}
                    >
                      <Typography variant="h6" color="primary" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                        {t('selected_category')}
                      </Typography>
                      <IconButton
                        size="small"
                        sx={{ display: { xs: 'block', md: 'none' } }}
                      >
                        {explanationExpanded ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </Box>

                    <Collapse in={explanationExpanded} timeout="auto">
                      <Box sx={{
                        maxHeight: { md: '320px' },
                        overflowY: 'auto',
                        pr: { md: 1 }
                      }}>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          sx={{ mb: 1, fontSize: '0.9rem' }}
                        >
                          {categoryDetails.group}
                        </Typography>

                        <Typography
                          variant="body1"
                          sx={{
                            mb: 2,
                            fontWeight: 500,
                            color: 'text.primary'
                          }}
                        >
                          {categoryDetails.name}
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            lineHeight: 1.6,
                            fontSize: '0.9rem'
                          }}
                        >
                          {categoryDetails.explanation}
                        </Typography>

                        <Box sx={{ mt: 2, p: 1, backgroundColor: 'primary.light', borderRadius: 1 }}>
                          <Typography variant="caption" color="white" sx={{ fontWeight: 500 }}>
                            💡 {t('tip')}: {t('category_tip_selected')}
                          </Typography>
                        </Box>
                      </Box>
                    </Collapse>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </TabPanel>

          {/* Tickets History Tab */}
          <TabPanel value={tabValue} index={1}>
            <SupportTickersList />
          </TabPanel>
        </Box>
      </div>
    </div>
  )
}

export default Support